import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { withDeadline } from "../../utils/withDeadline";
import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { getBaseReadProvider } from "../../utils/base-read-provider";
import { createNativeDepositSimulation } from "../../joinsplit-v2/deposit";
import {
  DEPOSIT_MAX_QUOTE_AGE_MS,
  quoteNativeDepositMax,
  type NativeDepositMaxQuote,
} from "../../joinsplit-v2/depositMax";

export function useDepositMax(params: {
  account: string;
  chainId: number;
  signer: ethers.Signer | null;
  token: TokenInfo | null;
  balance: string | null;
  amount: string;
  setAmount: (value: string) => void;
  /** Estimate after the user stops editing an open Add form. */
  prepare?: boolean;
}) {
  const [quote, setQuote] = useState<NativeDepositMaxQuote | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [error, setError] = useState("");
  const [edit, setEdit] = useState(0);
  const latest = useRef(params);
  latest.current = params;
  const maxMode = useRef(false);
  const accepted = useRef(false);
  const appliedAmount = useRef<string | null>(null);
  const revision = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const active = useRef<{
    controller: AbortController;
    promise: Promise<NativeDepositMaxQuote>;
  } | null>(null);
  const cached = useRef<{
    balance: string | null;
    quote: NativeDepositMaxQuote;
  } | null>(null);
  const scope = [
    params.account.toLowerCase(),
    params.chainId,
    params.token?.address,
    params.token?.tokenSubID,
    !!params.signer,
  ].join(":");
  const enabled = !!(
    params.prepare &&
    params.token?.isNative &&
    params.account &&
    params.signer
  );

  const stop = useCallback(() => {
    ++revision.current;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    active.current?.controller.abort();
    active.current = null;
  }, []);
  useEffect(() => {
    cached.current = null;
    maxMode.current = false;
    accepted.current = false;
    appliedAmount.current = null;
    setQuote(null);
    setError("");
    return stop;
  }, [scope, stop]);

  // Gas for the public dummy deposit is independent of the amount field. Join
  // its bounded, in-flight proof instead of destroying/restarting the worker on
  // every keystroke. Never use real wallet notes for this dry run.
  const capacity = useCallback(() => {
    const selection = latest.current;
    const saved = cached.current;
    if (
      saved &&
      saved.balance === selection.balance &&
      Date.now() >= saved.quote.quotedAt &&
      Date.now() - saved.quote.quotedAt < DEPOSIT_MAX_QUOTE_AGE_MS
    )
      return Promise.resolve(saved.quote);
    if (active.current) return active.current.promise;
    const controller = new AbortController();
    const work = {
      controller,
      promise: null! as Promise<NativeDepositMaxQuote>,
    };
    active.current = work;
    work.promise = withDeadline(
      async (signal) => {
        if (selection.chainId !== EVM_CONFIG.CHAIN_ID)
          throw new Error("Select the deposit network before estimating fees.");
        const provider = getBaseReadProvider();
        const simulation = await createNativeDepositSimulation({
          provider,
          account: selection.account,
          pool: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
          chainId: EVM_CONFIG.CHAIN_ID,
          signal,
        });
        if (signal.aborted)
          throw new DOMException("Estimate cancelled", "AbortError");
        return quoteNativeDepositMax(provider, simulation);
      },
      "The network fee estimate took too long. Edit the amount or select MAX to retry.",
      controller.signal,
      60_000,
    )
      .then((result) => {
        if (active.current === work)
          cached.current = { balance: selection.balance, quote: result };
        return result;
      })
      .finally(() => {
        if (active.current === work) active.current = null;
      });
    return work.promise;
  }, []);

  useEffect(() => {
    const id = ++revision.current;
    if (!enabled) {
      stop();
      setEstimating(false);
      return;
    }
    if (accepted.current) return;
    // Applying the gas reserve to MAX is a result, not another user edit.
    if (appliedAmount.current === params.amount) {
      appliedAmount.current = null;
      return;
    }
    appliedAmount.current = null;
    setQuote(null);
    setError("");
    let amount: bigint;
    try {
      amount = ethers.parseEther(params.amount);
    } catch {
      amount = 0n;
    }
    if (amount <= 0n) {
      setEstimating(false);
      return;
    }
    setEstimating(true);
    timer.current = setTimeout(async () => {
      timer.current = null;
      try {
        const result = await capacity();
        if (id !== revision.current || accepted.current) return;
        const selected = maxMode.current ? result.amount : amount;
        if (selected > result.amount)
          throw new Error(
            "Insufficient balance after network fees. Select MAX or enter a smaller amount.",
          );
        if (maxMode.current) {
          const value = ethers.formatEther(selected);
          if (value !== latest.current.amount) {
            appliedAmount.current = value;
            latest.current.setAmount(value);
          }
        }
        setQuote({ ...result, amount: selected });
      } catch (failure) {
        if (id === revision.current)
          setError(
            failure instanceof Error
              ? failure.message
              : "Could not estimate network fees. Edit the amount or select MAX to retry.",
          );
      } finally {
        if (id === revision.current) setEstimating(false);
      }
    }, AMOUNT_QUOTE_DEBOUNCE_MS);
    return () => {
      ++revision.current;
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [enabled, scope, params.amount, params.balance, edit, capacity, stop]);

  const changeAmount = (value: string) => {
    ++revision.current;
    maxMode.current = false;
    accepted.current = false;
    appliedAmount.current = null;
    setQuote(null);
    setError("");
    params.setAmount(value);
    setEdit((n) => n + 1);
  };
  const selectMax = () => {
    if (
      !params.token ||
      !params.signer ||
      !params.account ||
      params.balance === null
    )
      return;
    ++revision.current;
    accepted.current = false;
    maxMode.current = !!params.token.isNative;
    appliedAmount.current = null;
    setQuote(null);
    setError("");
    const saved = cached.current;
    const fresh =
      saved &&
      saved.balance === params.balance &&
      Date.now() >= saved.quote.quotedAt &&
      Date.now() - saved.quote.quotedAt < DEPOSIT_MAX_QUOTE_AGE_MS;
    // Change the field on click. Without a quote, start with the wallet balance;
    // deduct the gas reserve after the idle estimate, before allowing review.
    params.setAmount(
      params.token.isNative && fresh
        ? ethers.formatEther(saved!.quote.amount)
        : params.balance,
    );
    setEdit((n) => n + 1);
  };
  return {
    quote,
    estimating,
    error,
    selectMax,
    changeAmount,
    pending:
      enabled &&
      !accepted.current &&
      !!params.amount.trim() &&
      !quote &&
      !error,
    acceptAmount: () => {
      accepted.current = true;
      stop();
      setEstimating(false);
    },
  };
}
